import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { siteMeta } from '../data';
import { imageUrl } from '../lib/images';

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetch('/_api/public/content/services')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then(data => {
        const sorted = data.sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));
        setServices(sorted);
      })
      .catch(err => console.error('Error fetching services:', err));
  }, []);

  return (
    <>
      <Helmet>
        <title>Ydelser | {siteMeta.title}</title>
        <meta name="description" content="Se vores brede udvalg af ydelser inden for skovservice, træfældning og naturpleje." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-emerald-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img 
            src={imageUrl("/_assets/th-skovservice-ydelser-oversigt.jpg", 'width=1920,quality=85,format=auto')} 
            alt="TH Skovservice - Vores ydelser inden for skovservice, træfældning og naturpleje på Mors og i Thy" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/80 to-emerald-950" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-7xl font-bold mb-6 tracking-tight">Vores ydelser</h1>
            <p className="text-lg md:text-xl text-emerald-100/90 leading-relaxed max-w-2xl">
              Vi tilbyder en bred vifte af ydelser inden for skovservice og træpleje med fokus på sikkerhed og kvalitet. Fra den mindste haveopgave til store skovprojekter.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid - Styled like Home.jsx */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              // Determine if a card should be "large" (spanning 2 columns) for visual interest
              // We'll make the 1st and 7th items large on desktop
              const isLarge = index === 0 || index === 6;
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`${isLarge ? 'lg:col-span-2' : 'col-span-1'}`}
                >
                  <Link 
                    to={`/ydelser/${service.slug?.da}`} 
                    className="group relative block h-[400px] overflow-hidden bg-emerald-900"
                  >
                    <img
                      src={imageUrl(service.image?.[0], 'width=1000,quality=85,format=auto')}
                      alt={`${service.title?.da || 'Skovservice'} på Mors og i Thy - TH Skovservice`}
                      className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-70 transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-emerald-900/20 group-hover:bg-emerald-800/80 transition-colors duration-500" />
                    
                    <div className="absolute bottom-0 left-0 p-8 md:p-10 text-white z-10 w-full">
                      <h3 className={`${isLarge ? 'text-3xl md:text-4xl' : 'text-2xl'} font-bold tracking-tight mb-4`}>
                        {service.title?.da}
                      </h3>
                      <p className={`text-emerald-50/80 mb-6 max-w-md opacity-0 group-hover:opacity-100 transition-opacity duration-500 hidden md:block ${isLarge ? 'line-clamp-3' : 'line-clamp-2'}`}>
                        {service.short_description?.da}
                      </p>
                      <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest group-hover:translate-x-2 transition-transform">
                        Læs mere her <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 bg-stone-50 border-t border-stone-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6">Hvorfor vælge TH Skovservice?</h2>
            <p className="text-stone-600 text-lg">Vi kombinerer mange års erfaring med det nyeste udstyr for at sikre dig den bedste løsning.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                title: "Professionel rådgivning",
                desc: "Vi hjælper dig med at finde den rette løsning til netop dit behov og din have."
              },
              {
                title: "Sikkerhed i top",
                desc: "Vi er fuldt forsikrede og overholder alle sikkerhedskrav ved komplicerede opgaver."
              },
              {
                title: "Kvalitetsgaranti",
                desc: "Vi forlader aldrig en opgave før både vi og kunden er 100% tilfredse med resultatet."
              }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h4 className="text-xl font-bold mb-4">{item.title}</h4>
                <p className="text-stone-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-emerald-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-8 tracking-tight">Klar til at komme i gang?</h2>
          <p className="text-emerald-100 text-lg md:text-xl mb-12 max-w-2xl mx-auto">
            Kontakt os i dag for en uforpligtende snak om dit projekt og få et gratis tilbud.
          </p>
          <Link 
            to="/kontakt" 
            className="inline-flex items-center gap-2 px-10 py-5 bg-white text-emerald-900 font-bold hover:bg-emerald-50 transition-all transform hover:scale-105"
          >
            Kontakt os nu
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
}
